import { useMemo, useEffect } from 'react';
import { useAtom } from 'jotai';
import { makeGenericFieldAtom } from '../../store/genericFormStore';

/**
 * Where in the form store a field's value lives.
 *
 * @param {String} basePath - The schema's branch of the store.
 * @param {String} key - The field's key.
 *
 * @returns {String}
 */
export const getFullPath = (basePath, key) =>
  basePath ? `${basePath}.${key}` : key;

/**
 * A path in the form store, as the value held there and the setter that writes it back.
 *
 * The setter takes `{ isInit: true }` to write without marking the form as changed.
 *
 * @param {String} path - Where in the store the value lives.
 * @param {*} fallback - What the path reads as while the store holds nothing.
 *
 * @returns {Array} `[value, setValue]`.
 */
export const useFieldAtom = (path, fallback) => {
  const fieldAtom = useMemo(() => makeGenericFieldAtom(path, fallback), [path]);

  return useAtom(fieldAtom);
};

/**
 * A field's value in the form store, seeded from its descriptor.
 *
 * The store holds nothing until something puts it there, so a field seeds its own branch on mount
 * with the descriptor's `defaultValue` or, failing that, the empty value its type reads. Seeding is
 * silent, so an untouched form does not read as changed.
 *
 * @param {String} basePath - The schema's branch of the store.
 * @param {Object} field - The field descriptor.
 * @param {*} empty - The value the field holds when the descriptor declares none.
 *
 * @returns {Array} `[value, setValue, path]`.
 */
export const useFieldValue = (basePath, field, empty = null) => {
  const path = getFullPath(basePath, field.key);
  const [value, setValue] = useFieldAtom(path);

  useEffect(() => {
    if (value === undefined) {
      setValue(field.defaultValue ?? empty, { isInit: true });
    }
  }, [value, field.defaultValue, empty, setValue]);

  return [value, setValue, path];
};
